/**
 * `SIMEND.DAT`'s outcome sequence, as screens.
 *
 * The original answers *"how did you come down?"* before MISSION DEBRIEFING
 * answers *"how did it go?"*, on the layout `SIMEND.SCR` ships (still carrying
 * its placeholder title and lorem prose) and `safe-return.png` captures live:
 * a title in the header band, up to three lines at x=40, a framed photograph
 * between two engraved struts, and one CONTINUE. `packages/engine`'s
 * `simend.ts` chooses the sequence; this turns it into the screens.
 *
 * ## The picture codes, and how each was pinned
 *
 * `SIMEND.DAT` names its pictures with single letters. `PS.EXE` carries two
 * filename templates for them, consecutive in its data segment and immediately
 * before `simend.dat` itself: **`se-pictx`** and **`xjail-s` / `xhosp-s`**.
 *
 * The numbered family is pinned by a **gap that matches a gap**. The file uses
 * `e f g i j k` and never `h`; the volumes hold `SE-PICT1`, `2`, `3` and `5`
 * and never a `4`. Under `digit = letter − 'd'` the two absences land on the
 * same slot, which is one coincidence too many to be one:
 *
 * | code | sheet | what it shows | used by |
 * |---|---|---|---|
 * | `e` | `SE-PICT1` | an aeroplane going down trailing smoke | `CRASHED!!!`, both `VANQUISHED!` |
 * | `f` | `SE-PICT2` | wrecked aeroplanes on the ground | every *"You've crashed"* page |
 * | `g` | `SE-PICT3` | the pilot standing at his aerodrome | every safe return and return-to-duty page |
 * | `i` | `SE-PICT5` | a crowded ward of beds | *"taken to a nearby hospital"*, *"taken to a prison camp"* |
 *
 * `g` is pinned twice over: `docs/game/ui-reference/safe-return.png` draws
 * `SE-PICT3`'s **frame 1** under the line *"You land safely at your
 * aerodrome."*, which is `SIMEND.DAT`'s section 6 — the sheet, the frame and
 * the page, from a capture taken before any of this was decoded.
 *
 * `j` and `k` are the other template, and the `x` in `xjail-s` / `xhosp-s` is
 * the **nation letter**: the volumes hold `BJAIL-S`/`GJAIL-S` (a prisoner
 * against a wall) and `BHOSP-S`/`GHOSP-S` (a patient in a hospital bed). `j`
 * appears only on the prison-camp cards and `k` only on the hospital ones, so
 * the pairing is settled by the prose it sits in as well as by the name.
 *
 * **Frames are not a nation axis.** The four numbered sheets hold 4, 3, 2 and 3
 * frames; if frames encoded the two services every sheet would hold two. They
 * are variations, and nation is carried by the `x` templates instead — so a
 * German pilot and an English one see the same `SE-PICT3` and different
 * `xHOSP-S` sheets, which is what the asset names say. Frame 0 is used
 * throughout except for `g`, where the capture pins frame 1.
 *
 * The one place a frame moves is the *"...the time passes..."* card: it steps
 * to the sheet's **last** frame across its `$` pause. That is INFERRED, not
 * measured — but the three frames of `BHOSP-S` are a recovery (the head
 * bandage and the sling come off) and the three of `BJAIL-S` a decline, and
 * elapsed time is the card's entire subject.
 *
 * `SIMEND.BMP` is a fifth sheet, frame-for-frame the same subject as
 * `SE-PICT1`; no code in the file addresses it and nothing here draws it.
 */

import type { SimendTextPage, SimendTextSequence } from "@red-opus/engine";
import type { Rect } from "../render/draw-ops.ts";
import type { FontAtlas } from "../render/text-metrics.ts";
import { actionRow } from "./panel-parts.ts";
import type { MenuAction } from "./menu-model.ts";
import type { ShellScreen, Widget } from "./shell-screen.ts";

export type ShellNation = "german" | "allied";

interface PictureSheet {
  /** `sprites/…` for a sheet both services share, or a pair keyed by the nation letter. */
  readonly sheet: string | Readonly<Record<ShellNation, string>>;
  readonly frames: number;
  /** The frame drawn first. Only `g`'s is measured — see the module note. */
  readonly frame: number;
}

const PICTURES: Readonly<Record<string, PictureSheet>> = {
  e: { sheet: "sprites/SE-PICT1.BMP", frames: 4, frame: 0 },
  f: { sheet: "sprites/SE-PICT2.BMP", frames: 3, frame: 0 },
  g: { sheet: "sprites/SE-PICT3.BMP", frames: 2, frame: 1 },
  i: { sheet: "sprites/SE-PICT5.BMP", frames: 3, frame: 0 },
  j: { sheet: { allied: "sprites/BJAIL-S.BMP", german: "sprites/GJAIL-S.BMP" }, frames: 3, frame: 0 },
  k: { sheet: { allied: "sprites/BHOSP-S.BMP", german: "sprites/GHOSP-S.BMP" }, frames: 3, frame: 0 },
};

/**
 * Every sheet an outcome sequence can ask for. Both nations' `xJAIL-S`/`xHOSP-S`
 * are here because a surface declares its assets once, before it knows whose
 * career is about to end.
 */
export const SIMEND_SHEETS: readonly string[] = Object.values(PICTURES).flatMap((picture) =>
  typeof picture.sheet === "string" ? [picture.sheet] : Object.values(picture.sheet),
);

export interface SimendPicture {
  readonly sheet: string;
  readonly frame: number;
}

/** One CONTINUE press worth of the sequence: a title, a picture, and the lines visible so far. */
export interface SimendStep {
  readonly title: string;
  readonly picture: SimendPicture;
  readonly lines: readonly string[];
}

function pictureOf(code: string, nation: ShellNation, aged: boolean): SimendPicture {
  const picture = PICTURES[code];
  if (!picture) throw new Error(`simend: no sheet for picture code '${code}'`);
  const sheet = typeof picture.sheet === "string" ? picture.sheet : picture.sheet[nation];
  return { sheet, frame: aged ? picture.frames - 1 : picture.frame };
}

/**
 * A page's own steps. `$` inside a line is the original's pause: the page stops
 * with that line as its last, and CONTINUE brings the rest in **below** it
 * rather than replacing it — which is what makes *"The time passes slowly..."*
 * followed by *"...3 months..."* read as one card filling up.
 *
 * A page with no `$` is one step. A page with no lines at all is still one
 * step: `CRASHED!!!` opens on a picture and no prose.
 */
function pageSteps(page: SimendTextPage, title: string, nation: ShellNation): SimendStep[] {
  const steps: SimendStep[] = [];
  const shown: string[] = [];
  // `aged` is the far side of a pause: whatever comes after a `$` comes after
  // however long the card said it took, so the picture moves on with it.
  const step = (aged: boolean): SimendStep => ({ title, picture: pictureOf(page.picture, nation, aged), lines: [...shown] });
  for (const line of page.lines) {
    const pauses = line.includes("$");
    shown.push(line.replaceAll("$", ""));
    if (pauses) steps.push(step(false));
  }
  const last = steps[steps.length - 1];
  if (!last || last.lines.length !== shown.length) steps.push(step(steps.length > 0));
  return steps;
}

/** The whole sequence, flattened to the presses it takes to read it. */
export function simendSteps(sequence: SimendTextSequence, nation: ShellNation): readonly SimendStep[] {
  return sequence.pages.flatMap((page) => pageSteps(page, sequence.title, nation));
}

/** `safe-return.png`: the plate, the photograph and the two engraved side panels. */
export const SIMEND_PLATE: Rect = { x: 33, y: 26, w: 254, h: 147 };
export const SIMEND_PHOTO: Rect = { x: 88, y: 61, w: 144, h: 102 };

/** The two engraved side panels; measured as drawn geometry, not sprites — no frame in any sheet is 27×102. */
const SIMEND_STRUTS: readonly Rect[] = [
  { x: 48, y: 61, w: 27, h: 102 },
  { x: 245, y: 61, w: 27, h: 102 },
];

/**
 * The body text. x=40 and y=31 are `safe-return.png`'s, and the **pitch of 8**
 * is `SIMEND.SCR`'s: its placeholder paragraph draws three rows whose glyph
 * bands begin at y=31, 39 and 47, which is the only place in the evidence where
 * this screen carries more than one line.
 */
const SIMEND_TEXT = { x: 40, y: 31, pitch: 8 } as const;

export function outcomeScreen(step: SimendStep, font: FontAtlas, action: MenuAction, nation: ShellNation = "allied"): ShellScreen {
  return {
    id: "career/simend",
    chrome: { kind: "panel", title: step.title, emblem: nation === "german" ? "cross" : "roundel", background: undefined },
    widgets: [
      { kind: "plate", rect: SIMEND_PLATE },
      ...step.lines.map((line, index): Widget => ({
        kind: "text",
        text: line,
        x: SIMEND_TEXT.x,
        y: SIMEND_TEXT.y + index * SIMEND_TEXT.pitch,
        role: "label",
      })),
      ...SIMEND_STRUTS.map((rect): Widget => ({ kind: "plate", rect })),
      { kind: "well", rect: SIMEND_PHOTO },
      { kind: "picture", sheet: step.picture.sheet, frame: step.picture.frame, x: 90, y: 63 },
      ...actionRow([{ id: "continue", label: "CONTINUE", action }], font, 178),
    ],
    cancel: action,
  };
}
